'use client';

import { useMemo, useState } from 'react';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import { CarouselItem } from '@/components/@base/carousel';
import Container from '@/components/@base/container';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import XImage from '@/components/@base/x-image';
import ProductsCarousel from '@/components/app/home/components/@shared/carousel';
import SeeAllButton from '@/components/app/home/components/@shared/see-all-button';
import Filters from '@/components/app/home/components/ready-site/filters';
import Loading from '@/components/shared/components/cards/product/loading';
import ReadySiteCard from '@/components/shared/components/cards/readySite';
import RoundedIcon from '@/components/shared/components/icon/rounded';
import IconTextCombo from '@/components/shared/components/icon-text-combo';
import { loadingGenerator } from '@/components/shared/helpers';
import { useGetSearch } from '@/components/shared/services/hooks';

import { DATA_CY_DEMOS } from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { READY_SITE_FILTERS } from '@/constant/iterable-items';
import { pageLevelLocalization } from '@/constant/localization';

const readySiteId = process.env.NEXT_PUBLIC_READY_SITE_ID;

export default function ReadySite() {
  const [selectedTab, setSelectedTab] = useState(0);
  const searchParams = useMemo(() => {
    if (selectedTab !== 0) {
      return { tag_slugs: READY_SITE_FILTERS?.[selectedTab]?.value };
    }
    return { categories: [readySiteId!] };
  }, [selectedTab]);

  const { data, isLoading, isRefetching } = useGetSearch({
    params: searchParams,
    enabled: true,
  });
  const isDataLoading = isLoading || isRefetching;
  return (
    <Container
      center
      className='w-full bg-[#171922]! pt-2 pb-4 *:w-full md:mt-[75px] md:pt-12 md:pb-24'
    >
      <Container className='flex max-w-[1279px] flex-col justify-center gap-1 md:p-0'>
        <Container className='p-4 md:p-0'>
          <Container center>
            <IconTextCombo
              textSize={SIZE_ENUM.XL}
              Icon={
                <RoundedIcon
                  isDark
                  icon={
                    <XImage
                      src={imagesList.icons.monitor}
                      alt={pageLevelLocalization.home.takeYourBusinessOnline}
                    />
                  }
                />
              }
              headingType={HEADING_TYPE.H2}
              title={pageLevelLocalization.home.takeYourBusinessOnline}
              textClassName='font-bold text-xl md:text-[21px] text-white'
            />
            <SeeAllButton
              url={READY_SITE_FILTERS[selectedTab].listPath}
              theme='dark'
              className='h-[45px]!'
            />
          </Container>
          <Filters selectedTab={selectedTab} onTabChange={setSelectedTab} />
        </Container>

        <ProductsCarousel
          className='pr-4 md:px-4 md:pr-0'
          carouselOptions={{ loop: false, centeredSlides: false }}
          carouselClassName='overflow-visible! overflow-x-clip!'
        >
          {isDataLoading
            ? loadingGenerator(4, () => <Loading />).map((item, index) => (
                <CarouselItem key={index} className='w-[304px]!'>
                  {item}
                </CarouselItem>
              ))
            : Array.isArray(data) &&
              data?.map((item) => (
                <CarouselItem
                  key={item.id}
                  className='w-[304px]! hover:z-40'
                  data-cy={DATA_CY_DEMOS}
                >
                  <ReadySiteCard key={item.id} data={item} />
                </CarouselItem>
              ))}
        </ProductsCarousel>
      </Container>
    </Container>
  );
}
